import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function Article() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('简介');

  const drama = dramas.find((d) => d.id === Number(id));

  useEffect(() => {
    if (drama) {
      document.title = `${drama.title} - 6080影视 | ${drama.category}高清在线观看`;
    }
  }, [drama]);

  if (!drama) {
    return (
      <>
        <ScrollToTop />
        <main className="min-h-screen pt-24 flex flex-col items-center justify-center">
          <i className="ri-error-warning-line text-5xl text-foreground-600 mb-4"></i>
          <h1 className="text-xl text-foreground-200 mb-2">剧集未找到</h1>
          <Link to="/" className="text-primary-500 hover:underline cursor-pointer">返回首页</Link>
        </main>
      </>
    );
  }

  const relatedDramas = dramas
    .filter((d) => d.categorySlug === drama.categorySlug && d.id !== drama.id)
    .slice(0, 6);

  const breadcrumbs = [
    { name: '首页', href: '/' },
    { name: drama.category, href: `/category/${drama.categorySlug}` },
    { name: drama.title, href: '' },
  ];

  const episodeList = Array.from({ length: drama.episodeCount }, (_, i) => i + 1);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${drama.title} - 6080影视`,
    description: drama.description,
    url: `https://www.6080ys.com/article/${drama.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '6080影视',
      url: 'https://www.6080ys.com/',
    },
  };

  const tvSeriesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TVSeries',
    name: drama.title,
    description: drama.description,
    url: `https://www.6080ys.com/article/${drama.id}`,
    image: drama.cover,
    genre: drama.category,
    numberOfEpisodes: drama.episodeCount,
    actor: drama.actors.map((actor) => ({ '@type': 'Person', name: actor })),
    director: { '@type': 'Person', name: drama.director },
    countryOfOrigin: { '@type': 'Country', name: drama.region },
    datePublished: `${drama.year}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: drama.rating,
      bestRating: '10',
      ratingCount: 1000,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.href ? `https://www.6080ys.com${crumb.href}` : undefined,
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(tvSeriesJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      <main className="min-h-screen pt-20">
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <ol className="flex items-center gap-2 text-sm flex-wrap">
            {breadcrumbs.map((crumb, index) => (
              <li key={crumb.name} className="flex items-center">
                {index > 0 && <span className="text-foreground-600 mr-2">&gt;</span>}
                {crumb.href ? (
                  <Link to={crumb.href} className="text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer">{crumb.name}</Link>
                ) : (
                  <span className="text-foreground-300">{crumb.name}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Drama header */}
          <div className="flex flex-col md:flex-row gap-6 mb-8">
            <div className="relative rounded-xl overflow-hidden flex-shrink-0 mx-auto md:mx-0" style={{ width: '260px', aspectRatio: '2/3' }}>
              <img
                src={drama.cover}
                alt={drama.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 left-3">
                <span className="bg-primary-500 text-background-950 text-sm font-bold px-3 py-1 rounded-md">{drama.rating}</span>
              </div>
              <div className="absolute top-3 right-3">
                <span className={`text-sm px-3 py-1 rounded-md ${
                  drama.status === '更新中'
                    ? 'bg-accent-500/90 text-foreground-50'
                    : 'bg-background-900/70 text-foreground-300'
                }`}>{drama.status}</span>
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-3">{drama.title}</h1>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full">{drama.category}</span>
                <span className="text-xs bg-background-200 text-foreground-500 px-3 py-1 rounded-full">{drama.region}</span>
                <span className="text-xs bg-background-200 text-foreground-500 px-3 py-1 rounded-full">{drama.year}年</span>
                <span className="text-xs bg-background-200 text-foreground-500 px-3 py-1 rounded-full">共{drama.episodeCount}集</span>
              </div>

              <div className="space-y-2 mb-5">
                <div className="flex items-start gap-2">
                  <span className="text-xs text-foreground-500 w-14 shrink-0">导演:</span>
                  <span className="text-sm text-foreground-300">{drama.director}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-xs text-foreground-500 w-14 shrink-0">主演:</span>
                  <span className="text-sm text-foreground-300">{drama.actors.join(' / ')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-xs text-foreground-500 w-14 shrink-0">更新:</span>
                  <span className="text-sm text-primary-500 font-medium">{drama.updateInfo}</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button className="px-6 py-2.5 bg-primary-500 text-background-950 font-medium rounded-full text-sm hover:bg-primary-600 transition-all duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-line"></i>开始观看
                </button>
                <button className="px-6 py-2.5 border border-background-400 text-foreground-300 font-medium rounded-full text-sm hover:bg-background-200 transition-all duration-300 cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i>收藏
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-background-300/60">
            {['简介', '分集', '评论'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? 'text-primary-500 border-primary-500'
                    : 'text-foreground-500 border-transparent hover:text-foreground-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="mb-12">
            {activeTab === '简介' && (
              <div className="bg-background-50 rounded-xl p-6 border border-background-300/60">
                <h2 className="text-lg font-heading font-bold text-foreground-100 mb-4">剧情简介</h2>
                <p className="text-sm text-foreground-300 leading-relaxed">{drama.description}</p>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-foreground-500">类型</span>
                    <p className="text-sm text-foreground-300 mt-1">{drama.category}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">地区</span>
                    <p className="text-sm text-foreground-300 mt-1">{drama.region}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">年份</span>
                    <p className="text-sm text-foreground-300 mt-1">{drama.year}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">集数</span>
                    <p className="text-sm text-foreground-300 mt-1">{drama.episodeCount}集</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">状态</span>
                    <p className="text-sm text-foreground-300 mt-1">{drama.status}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">评分</span>
                    <p className="text-sm text-primary-500 font-medium mt-1">{drama.rating}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === '分集' && (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                {episodeList.map((ep) => (
                  <button
                    key={ep}
                    className="bg-background-50 rounded-lg py-3 px-2 text-center text-sm text-foreground-300 hover:bg-primary-500/20 hover:text-primary-500 border border-background-300/60 transition-all duration-200 cursor-pointer"
                  >
                    <span className="block text-xs text-foreground-500 mb-1">第{ep}集</span>
                    <i className="ri-play-circle-line text-lg"></i>
                  </button>
                ))}
              </div>
            )}

            {activeTab === '评论' && (
              <div className="bg-background-50 rounded-xl p-6 border border-background-300/60">
                <div className="text-center py-10">
                  <i className="ri-message-3-line text-3xl text-foreground-600 mb-3"></i>
                  <p className="text-sm text-foreground-500">登录后可发表评论</p>
                </div>
              </div>
            )}
          </div>

          {/* Related dramas */}
          {relatedDramas.length > 0 && (
            <div className="mb-12">
              <h2 className="text-lg font-heading font-bold text-foreground-100 mb-6">相关推荐</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                {relatedDramas.map((d) => (
                  <Link
                    key={d.id}
                    to={`/article/${d.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative rounded-lg overflow-hidden mb-2" style={{ aspectRatio: '2/3' }}>
                      <img
                        src={d.cover}
                        alt={d.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute top-1 left-1">
                        <span className="bg-primary-500/90 text-background-950 text-[10px] font-bold px-1.5 py-0.5 rounded">{d.rating}</span>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-300 group-hover:text-primary-500 transition-colors duration-200 truncate">{d.title}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}